import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../../src/lib/supabase';
import type { EmissaoFaturamento } from '../../../../src/lib/financeiro/metricasFaturamento';
import { achatarEmissoes, type FaturaBrutaEmissao } from '../lib/achatarEmissoes';

const COLUNAS =
  'id,uc_codigo,cliente_nome,mes_referencia,data_vencimento,classificacao,created_at,' +
  'emitido_em,emitido_por_nome,historico_emissoes,empresa_parceira_id';

/**
 * Emissões dos boletos da Vigor, já achatadas.
 *
 * `desde` corta no servidor as faturas cuja última emissão é anterior à data —
 * seguro porque `emitido_em` é a emissão MAIS RECENTE: se a primeira caiu depois
 * de `desde`, a última também caiu. Um dia de folga cobre a diferença entre o
 * relógio do navegador (`emitido_em`) e o do servidor (`registrado_em`); o
 * recorte exato fica com quem chama. Usado pelo BI de equipes, que só precisa
 * do período escolhido; sem `desde` vem a carteira inteira.
 */
export async function buscarEmissoes(desde?: Date | null): Promise<EmissaoFaturamento[]> {
  let todas: FaturaBrutaEmissao[] = [];
  let inicio = 0;
  const tamanhoPagina = 1000;

  while (true) {
    let consulta = supabase
      .from('faturas')
      .select(COLUNAS)
      .eq('ambiente', 'producao')
      .neq('status_fatura', 'Excluído')
      // "Boleto da Vigor": fatura de empresa parceira tem outro dono e outro
      // gateway, e o prazo de faturamento cobrado aqui é o do time interno.
      .is('empresa_parceira_id', null)
      .not('emitido_em', 'is', null);
    if (desde) consulta = consulta.gte('emitido_em', new Date(desde.getTime() - 86_400_000).toISOString());

    const { data, error } = await consulta
      .order('id')
      .range(inicio, inicio + tamanhoPagina - 1);

    if (error) throw error;
    if (!data || data.length === 0) break;
    todas = todas.concat(data as unknown as FaturaBrutaEmissao[]);
    if (data.length < tamanhoPagina) break;
    inicio += tamanhoPagina;
  }

  return todas.flatMap(achatarEmissoes);
}

/**
 * Nomes do time de Faturamento. É por `profiles.departamento` (a taxonomia que a
 * `permissaoEmissaoParceira` já usa), nunca por lista de nomes no código — quando
 * entrar alguém novo no time, o dashboard acompanha sozinho.
 */
async function buscarTimeDeFaturamento(): Promise<string[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('full_name,departamento,departamentos')
    .is('empresa_parceira_id', null);

  if (error) throw error;

  return (data || [])
    .filter((p: any) =>
      p.departamento === 'Faturamento'
      || (Array.isArray(p.departamentos) && p.departamentos.includes('Faturamento')))
    .map((p: any) => p.full_name)
    .filter(Boolean);
}

export function useDashboardFaturamentoData() {
  const [emissoes, setEmissoes] = useState<EmissaoFaturamento[]>([]);
  const [timeFaturamento, setTimeFaturamento] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const [lista, time] = await Promise.all([buscarEmissoes(), buscarTimeDeFaturamento()]);
      setEmissoes(lista);
      setTimeFaturamento(time);
      setUltimaAtualizacao(new Date());
    } catch (e: any) {
      console.error('Erro ao carregar o Dashboard do Faturamento:', e);
      setErro(e?.message || 'Erro ao carregar os dados do dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  return { emissoes, timeFaturamento, loading, erro, ultimaAtualizacao, refetch: carregar };
}
